/**
 * On-device embeddings using bge-small-en-v1.5 via transformers.js.
 * 384-dim vectors, ~30MB model. Cached in browser after first load.
 */

let pipePromise: Promise<any> | null = null;

export type EmbedProgress = { status: 'init' | 'downloading' | 'ready'; pct: number };

export async function loadEmbedder(onProgress?: (p: EmbedProgress) => void): Promise<any> {
  if (pipePromise) return pipePromise;
  pipePromise = (async () => {
    onProgress?.({ status: 'init', pct: 0 });
    const { pipeline } = await import('@huggingface/transformers');
    const pipe = await pipeline('feature-extraction', 'Xenova/bge-small-en-v1.5', {
      progress_callback: (p: any) => {
        if (p.status === 'progress' && p.total) onProgress?.({ status: 'downloading', pct: Math.round((p.loaded / p.total) * 100) });
        else if (p.status === 'done') onProgress?.({ status: 'downloading', pct: 100 });
      },
    } as any);
    onProgress?.({ status: 'ready', pct: 100 });
    return pipe;
  })().catch((e) => { pipePromise = null; throw e; });
  return pipePromise;
}

export async function embed(texts: string[]): Promise<Float32Array[]> {
  const pipe = await loadEmbedder();
  const out: Float32Array[] = [];
  for (const t of texts) {
    const r = await pipe(t, { pooling: 'mean', normalize: true });
    out.push(new Float32Array(r.data));
  }
  return out;
}

export function cosine(a: Float32Array, b: Float32Array): number {
  let s = 0;
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) s += a[i] * b[i];
  return s; // inputs are normalized, so dot = cosine
}

export type ScoredChunk = { id: string; book_id: string; text: string; score: number };

export function topK(query: Float32Array, items: { id: string; book_id: string; text: string; vec: Float32Array }[], k = 5): ScoredChunk[] {
  return items
    .map((c) => ({ id: c.id, book_id: c.book_id, text: c.text, score: cosine(query, c.vec) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, k);
}
